import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  CreditCard,
  Shield,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Loader2,
  Copy,
  ExternalLink,
  AlertTriangle,
  BookOpen,
} from "lucide-react";
import confetti from "canvas-confetti";

import paymentService from "@/services/paymentService";

const CURRENCIES = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  AED: "د.إ",
  INR: "₹",
  CAD: "C$",
  AUD: "A$",
  SGD: "S$",
};

const PURPOSE_LABELS = {
  CROWD_CONTRIBUTION: "Crowdfund Contribution",
  INDIVIDUAL_FUNDING: "Individual Funding",
  ENCRYPTION_ADDON: "Encryption Add-on",
};

export default function PaymentCheckout() {
  const navigate = useNavigate();
  const { intentId } = useParams();

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [intent, setIntent] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);

  useEffect(() => {
    loadIntent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intentId]);

  const loadIntent = async () => {
    try {
      setLoading(true);
      const data = await paymentService.getIntent(intentId);
      setIntent(data);

      // If already succeeded, show success state
      if (data.status === "SUCCEEDED") {
        setPaymentResult({ ok: true, status: "SUCCEEDED" });
      }
    } catch (error) {
      console.error("Error loading intent:", error);
      if (error.response?.status === 404) {
        toast.error("Payment not found");
        navigate("/participate");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    try {
      setProcessing(true);
      const result = await paymentService.confirmMock(intentId, "success");
      setPaymentResult(result);

      if (result.ok) {
        // Celebration animation
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        });
        toast.success("Payment successful!");
      }
    } catch (error) {
      console.error("Error confirming payment:", error);
      setPaymentResult({
        ok: false,
        status: "FAILED",
        message: error.response?.data?.detail || "Payment failed",
      });
      toast.error("Payment failed. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    try {
      await paymentService.cancelIntent(intentId);
      toast.info("Payment canceled");
      navigate(intent?.return_url || "/participate");
    } catch (error) {
      console.error("Error canceling:", error);
      navigate("/participate");
    }
  };

  const handleCopyReference = () => {
    if (paymentResult?.reference) {
      navigator.clipboard.writeText(paymentResult.reference);
      toast.success("Reference copied to clipboard");
    }
  };

  const getCurrencySymbol = () => {
    return CURRENCIES[intent?.currency] || "$";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto" />
          <p className="mt-4 text-slate-600">Loading checkout...</p>
        </div>
      </div>
    );
  }

  if (!intent) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="py-12 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-black mb-2">
              Payment Not Found
            </h2>
            <Button onClick={() => navigate("/participate")} variant="outline">
              Back to Participate
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success State
  if (paymentResult?.ok) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="payment-success">
        <header className="bg-white border-b border-slate-200">
          <div className="container mx-auto px-6 py-4">
            <h1
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Golden Records
            </h1>
          </div>
        </header>

        <main className="container mx-auto px-6 py-12">
          <div className="max-w-xl mx-auto">
            <Card className="bg-white border-2 border-green-200 shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>

                <h2
                  className="text-2xl font-bold text-black mb-2"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Payment Successful!
                </h2>
                <p className="text-slate-600 mb-6">
                  Thank you for your contribution
                </p>

                {/* Receipt */}
                <div className="bg-slate-50 rounded-lg p-6 text-left mb-6">
                  <h3 className="font-semibold text-black mb-4">
                    Payment Receipt
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Amount</span>
                      <span className="font-medium text-black">
                        {getCurrencySymbol()}
                        {intent.total_amount?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Purpose</span>
                      <span className="font-medium text-black">
                        {PURPOSE_LABELS[intent.purpose]}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Status</span>
                      <Badge className="bg-green-100 text-green-800">
                        Completed
                      </Badge>
                    </div>
                    {paymentResult.reference && (
                      <div className="flex justify-between items-center pt-2 border-t">
                        <span className="text-slate-600">Reference</span>
                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-white px-2 py-1 rounded">
                            {paymentResult.reference}
                          </code>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleCopyReference}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {intent.encryption_included && (
                  <div className="flex items-center justify-center gap-2 text-green-600 mb-6">
                    <Shield className="w-4 h-4" />
                    <span className="text-sm">Encryption included</span>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    onClick={() => navigate(intent.return_url || "/participate")}
                    className="flex-1 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500"
                    data-testid="return-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Return to Project
                  </Button>
                  <Button
                    onClick={() => navigate("/payments/history")}
                    variant="outline"
                    className="flex-1"
                    data-testid="history-btn"
                  >
                    View History
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  // Failed State
  if (paymentResult && !paymentResult.ok) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="payment-failed">
        <header className="bg-white border-b border-slate-200">
          <div className="container mx-auto px-6 py-4">
            <h1
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Golden Records
            </h1>
          </div>
        </header>

        <main className="container mx-auto px-6 py-12">
          <div className="max-w-xl mx-auto">
            <Card className="bg-white border-2 border-red-200 shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>

                <h2 className="text-2xl font-bold text-black mb-2">
                  Payment Failed
                </h2>
                <p className="text-slate-600 mb-6">
                  {paymentResult.message || "Something went wrong. Please try again."}
                </p>

                <div className="flex gap-3">
                  <Button
                    onClick={handleConfirmPayment}
                    className="flex-1 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500"
                  >
                    Try Again
                  </Button>
                  <Button
                    onClick={handleCancel}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  // Checkout State
  return (
    <div className="min-h-screen bg-slate-50" data-testid="payment-checkout">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1
                className="text-2xl font-bold text-slate-900"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Golden Records
              </h1>
              <Badge className="bg-yellow-100 text-yellow-800 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Test Mode (Mock Provider)
              </Badge>
            </div>
            <Button variant="outline" onClick={handleCancel}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12">
        <div className="max-w-2xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Order Summary */}
            <div className="lg:col-span-3">
              <Card className="bg-white border-2 border-slate-200 shadow-lg">
                <CardHeader>
                  <CardTitle
                    className="text-xl"
                    style={{ fontFamily: "Playfair Display, serif" }}
                  >
                    Order Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Purpose Badge */}
                  <div className="flex items-center justify-between">
                    <Badge className="bg-purple-100 text-purple-800">
                      {PURPOSE_LABELS[intent.purpose]}
                    </Badge>
                    <Badge className="bg-slate-200 text-slate-900 border-slate-300">
                    {intent.entity_type}
                    </Badge>
                  </div>

                  {/* Entity Info */}
                  {intent.entity_snapshot && Object.keys(intent.entity_snapshot).length > 0 && (
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-start gap-3">
                        <BookOpen className="w-5 h-5 text-purple-600 mt-1" />
                        <div>
                          <h4 className="font-semibold text-black">
                            {intent.entity_snapshot.title || "Untitled"}
                          </h4>
                          {intent.entity_snapshot.author && (
                            <p className="text-sm text-slate-600">
                              by {intent.entity_snapshot.author}
                            </p>
                          )}
                          {intent.entity_snapshot.language && (
                            <Badge className="mt-2 text-xs bg-blue-100 text-blue-800 border-blue-200">
                              {intent.entity_snapshot.language}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Payer Info */}
                  <div className="space-y-2">
                    <p className="text-sm text-slate-600">Paying as:</p>
                    <p className="font-medium text-black">
                      {intent.payer_name || intent.payer_email}
                    </p>
                    {intent.payer_name && (
                      <p className="text-sm text-slate-500">{intent.payer_email}</p>
                    )}
                  </div>

                  {/* Encryption Status */}
                  {intent.encryption_included && (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-medium text-green-800">
                            Encryption Included
                          </p>
                          <p className="text-sm text-green-600">
                            Your contribution includes encryption at no extra cost
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {intent.encryption_opt_in && intent.encryption_fee > 0 && (
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-purple-600" />
                        <div>
                          <p className="font-medium text-purple-800">
                            Encryption Add-on
                          </p>
                          <p className="text-sm text-purple-600">
                            +{getCurrencySymbol()}
                            {intent.encryption_fee?.toFixed(2)} for enhanced security
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Payment Card */}
            <div className="lg:col-span-2">
              <Card className="bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 border-2 border-purple-200 shadow-lg">
                <CardContent className="p-6 space-y-6">
                  <div className="text-center">
                    <p className="text-sm text-slate-600 mb-1">Total Amount</p>
                    <p className="text-4xl font-bold text-black">
                      {getCurrencySymbol()}
                      {intent.total_amount?.toFixed(2)}
                    </p>
                    <p className="text-sm text-slate-500">{intent.currency}</p>
                  </div>

                  {/* Breakdown */}
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Base Amount</span>
                      <span className="text-black">
                        {getCurrencySymbol()}
                        {intent.amount?.toFixed(2)}
                      </span>
                    </div>
                    {intent.encryption_fee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Encryption</span>
                        <span className="text-black">
                          {getCurrencySymbol()}
                          {intent.encryption_fee?.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Pay Button */}
                  <Button
                    onClick={handleConfirmPayment}
                    disabled={processing || intent.status !== "CREATED"}
                    className="w-full bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 py-6 text-lg"
                    data-testid="pay-btn"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        Pay (Test Mode)
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-slate-500">
                    This is a test payment. No real charges will be made.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
